// Persona handling for the full-screen preview.
//
// The live runner never sees who a rule targets - the server replaces each
// participant/attribute condition with a { fact } the browser just looks up. The
// preview, though, reads the raw rules from the builder, so it evaluates those
// conditions itself against a persona the author chooses, then projects them to
// the same { fact } shape the shared evaluator already understands. Answer
// conditions are left untouched.
//
// A persona is { participantType?: 'staff'|'pupil'|'parent',
//                attributes?: { [key]: value } }. A missing value is "unknown"
// and never holds (matching the live semantics for a missing fact).
//
// Items are in the shared evaluator shape: { id, kind, rules? } with kind 2 for
// skip-logic sections.

import {ATTRIBUTE_KEYS, PARTICIPANT_TYPES, attributeValueLabel} from './conditions'

const SKIP_LOGIC = 2

// Maps a survey AUDIENCE value (staff 1 / parent 2 / student 4) to the persona
// participant type used for rule evaluation, with a label for the picker. Lets a
// multi-audience survey be previewed as each of its audiences, even
// when no rule references the participant type.
const AUDIENCE_PERSONA = {
  1: {audience: 1, participantType: 'staff', label: 'Staff'},
  2: {audience: 2, participantType: 'parent', label: 'Parent'},
  4: {audience: 4, participantType: 'pupil', label: 'Student'},
}

export const audiencePersonas = (audiences = []) =>
  (Array.isArray(audiences) ? audiences : [])
    .map(a => AUDIENCE_PERSONA[a])
    .filter(Boolean)

const asStrings = value =>
  value == null ? [] : (Array.isArray(value) ? value : [value]).map(String)

const SET_OP_LABEL = {isAnyOf: 'is any of', isNoneOf: 'is none of'}

// A readable description of a participant/attribute condition, for the route
// trace (where the projected rule only carries an opaque fact key). Mirrors the
// labels the condition editor shows.
const describePersonaCondition = cond => {
  if (cond?.participant) {
    const op = SET_OP_LABEL[cond.participant.op] || cond.participant.op
    const names = (cond.participant.values || []).map(
      v => PARTICIPANT_TYPES.find(p => p.value === v)?.label || v
    )
    return `Participant ${op} ${names.join(', ') || '(none)'}`
  }
  if (cond?.attribute) {
    const spec = ATTRIBUTE_KEYS.find(k => k.key === cond.attribute.key)
    const op = SET_OP_LABEL[cond.attribute.op] || cond.attribute.op
    const values = (cond.attribute.values || []).map(v => attributeValueLabel(cond.attribute.key, v)).join(', ')
    return `${spec?.label || cond.attribute.key} ${op} ${values || '(none)'}`
  }
  return 'Participant condition'
}

// Does a set operator (isAnyOf / isNoneOf) hold for the persona's value(s)? Values
// are compared trimmed and without regard to case, as the server compares them.
const normalise = value => String(value).trim().toLowerCase()
const setHolds = (op, personaValues, conditionValues) => {
  const wanted = conditionValues.map(normalise)
  const has = personaValues.some(v => wanted.includes(normalise(v)))
  return op === 'isNoneOf' ? !has : has
}

export const evalPersonaCondition = (cond, persona = {}) => {
  if (cond?.participant) {
    const values = persona.participantType ? [String(persona.participantType)] : []
    if (!values.length) return false
    return setHolds(cond.participant.op, values, cond.participant.values || [])
  }
  if (cond?.attribute) {
    const values = asStrings(persona.attributes?.[cond.attribute.key]).filter(v => v.trim() !== '')
    if (!values.length) return false
    return setHolds(cond.attribute.op, values, cond.attribute.values || [])
  }
  return false
}

// Which persona inputs the picker should ask for: whether any rule tests the
// participant type, and the attribute keys any rule references (so unreferenced
// attributes are not asked for).
export const personaInputs = items => {
  const attributeKeys = new Set()
  let usesParticipantType = false
  const scan = cond => {
    if (!cond) return
    if (cond.all || cond.any) (cond.all || cond.any).forEach(scan)
    else if (cond.participant) usesParticipantType = true
    else if (cond.attribute?.key) attributeKeys.add(cond.attribute.key)
  }
  ;(Array.isArray(items) ? items : []).forEach(item => {
    if (item?.kind === SKIP_LOGIC) {
      ;(item.rules?.rules || []).forEach(rule => scan(rule.when))
    }
  })
  return {usesParticipantType, attributeKeys: [...attributeKeys]}
}

// Project a condition tree, replacing each participant/attribute leaf with a
// { fact: key } and recording whether that fact holds for the persona. Answer
// conditions and the group structure are preserved. Keys mirror the server's
// <sectionId>.<ruleId>.<path> scheme so the two never collide.
const projectCondition = (cond, persona, facts, labels, keyBase) => {
  if (cond?.all || cond?.any) {
    const mode = cond.all ? 'all' : 'any'
    return {
      [mode]: cond[mode].map((c, i) =>
        projectCondition(c, persona, facts, labels, `${keyBase}.${i}`)
      ),
    }
  }
  if (cond?.participant || cond?.attribute) {
    facts[keyBase] = evalPersonaCondition(cond, persona)
    labels[keyBase] = describePersonaCondition(cond)
    return {fact: keyBase}
  }
  return cond
}

// Project every section's rules for the persona and collect the facts, so the
// shared evaluator can be run: resolveRoute(items, answers, facts). factLabels
// maps each fact key to a readable description, so the route trace can show what
// the projected persona condition was.
export const projectForPersona = (items, persona = {}) => {
  const facts = {}
  const factLabels = {}
  const projected = (Array.isArray(items) ? items : []).map(item => {
    if (item?.kind !== SKIP_LOGIC || !item.rules?.rules) return item
    return {
      ...item,
      rules: {
        ...item.rules,
        rules: item.rules.rules.map(rule => ({
          ...rule,
          when: projectCondition(rule.when, persona, facts, factLabels, `${item.id}.${rule.id}`),
        })),
      },
    }
  })
  return {items: projected, facts, factLabels}
}
